/**
 * money.ts — all amounts are stored as INTEGER AGOROT (₪1 = 100).
 * Never store floats. Convert at the edges (UI input, Nedarim API, CSV).
 */

export type Agorot = number;

/** "1,234.50" | "1 234,50" | "180" | 180.5 → 123450 */
export function toAgorot(input: string | number): Agorot {
  if (typeof input === 'number') return Math.round(input * 100);
  let s = String(input).trim().replace(/[₪\s]/g, '');
  if (s === '') return 0;
  const neg = /^-|-$|^\(.*\)$/.test(s);
  s = s.replace(/[-()]/g, '');
  // If both separators exist, the last one is the decimal separator.
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma > -1 && lastDot > -1) {
    const dec = lastComma > lastDot ? ',' : '.';
    const thou = dec === ',' ? '.' : ',';
    s = s.split(thou).join('').replace(dec, '.');
  } else if (lastComma > -1) {
    // "1,234" (thousands) vs "12,5" (decimal): decimal if 1–2 digits after comma
    s = /,\d{1,2}$/.test(s) ? s.replace(',', '.') : s.split(',').join('');
  }
  const n = Number(s);
  if (!Number.isFinite(n)) throw new Error(`Invalid amount: ${input}`);
  return (neg ? -1 : 1) * Math.round(n * 100);
}

export function fromAgorot(a: Agorot): number {
  return a / 100;
}

const fmtWhole = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 0 });
const fmtCents = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 18000 → "‏180 ₪", 123450 → "‏1,234.50 ₪" */
export function formatILS(a: Agorot): string {
  return (a % 100 === 0 ? fmtWhole : fmtCents).format(a / 100);
}

/** For Nedarim API params: 123450 → "1234.50" */
export function agorotToApi(a: Agorot): string {
  return (a / 100).toFixed(2);
}

export function sumAgorot(xs: Agorot[]): Agorot {
  return xs.reduce((s, x) => s + x, 0);
}
